import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import cetasolLogo from "@/assets/cetasol-logo.png";

import { smoothScrollToTop } from "@/lib/smoothScroll";

const Header = () => {
  const { t, i18n } = useTranslation('common');
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [insightOpen, setInsightOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const [mobileInsightOpen, setMobileInsightOpen] = useState(false);

  const currentLang = i18n.language?.startsWith('sv') ? 'sv' : i18n.language?.startsWith('ja') ? 'ja' : i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const languages = [
    { code: 'en', label: 'English', flag: 'https://flagcdn.com/w40/ca.png' },
    { code: 'sv', label: 'Svenska', flag: 'https://flagcdn.com/w40/se.png' },
    { code: 'ja', label: '日本語', flag: 'https://flagcdn.com/w40/jp.png' },
    { code: 'zh', label: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
  ];

  const solutionDropdown = [
    { label: t('solutionDropdown.ihelm'), href: "/ihelm-intelligent-platform", description: t('solutionDropdown.ihelmDesc') },
    { label: t('solutionDropdown.cetafuel'), href: "/cetafuel", description: t('solutionDropdown.cetafuelDesc') },
    { label: t('solutionDropdown.cetanav'), href: "/cetanav", description: t('solutionDropdown.cetanavDesc') },
  ];

  const companyDropdown = [
    { label: t('companyDropdown.aboutUs'), href: "/about", description: t('companyDropdown.aboutUsDesc') },
    { label: t('companyDropdown.contact'), href: "/contact", description: t('companyDropdown.contactDesc') },
  ];

  const resourcesDropdown = [
    { label: t('resourcesDropdown.blogPost'), href: "/insight/blog-post", description: t('resourcesDropdown.blogPostDesc') },
    { label: t('resourcesDropdown.news'), href: "/news", description: t('resourcesDropdown.newsDesc') },
    { label: t('resourcesDropdown.pressRelease'), href: "/insight/press-release", description: t('resourcesDropdown.pressReleaseDesc') },
    { label: t('resourcesDropdown.whitePapers'), href: "/whitepapers", description: t('resourcesDropdown.whitePapersDesc') },
    { label: t('resourcesDropdown.projects'), href: "/projects", description: t('resourcesDropdown.projectsDesc') },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
      ? "bg-sidebar/80 backdrop-blur-xl border-b border-border/30"
          : "bg-sidebar backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-6">
        <Link to="/" onClick={(e) => {
          if (window.location.pathname === '/') {
            e.preventDefault();
            smoothScrollToTop(1200);
          }
        }}>
          <img src={cetasolLogo} alt="Cetasol - Maritime Monitoring and Analytics Platform" className="h-8" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {/* Solutions dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
            >
              <Link
                to="/solutions"
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              >
                {t('nav.solutions')}
                <ChevronDown size={14} className={`transition-transform duration-200 ${solutionsOpen ? "rotate-180" : ""}`} />
              </Link>

            <AnimatePresence>
              {solutionsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="bg-[#162231] border border-border rounded-lg shadow-lg p-2 min-w-[200px]">
                    {solutionDropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="flex flex-col gap-0.5 px-4 py-2.5 rounded-md hover:bg-[#1C2838] transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Company dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCompanyOpen(true)}
            onMouseLeave={() => setCompanyOpen(false)}
          >
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
            >
              {t('nav.company')}
              <ChevronDown size={14} className={`transition-transform duration-200 ${companyOpen ? "rotate-180" : ""}`} />
            </Link>

            <AnimatePresence>
              {companyOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="bg-[#162231] border border-border rounded-lg shadow-lg p-2 min-w-[220px]">
                    {companyDropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="flex flex-col gap-0.5 px-4 py-2.5 rounded-md hover:bg-[#1C2838] transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Resources dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setInsightOpen(true)}
            onMouseLeave={() => setInsightOpen(false)}
          >
            <Link
              to="/news"
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
            >
              {t('nav.resources')}
              <ChevronDown size={14} className={`transition-transform duration-200 ${insightOpen ? "rotate-180" : ""}`} />
            </Link>

            <AnimatePresence>
              {insightOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="bg-[#162231] border border-border rounded-lg shadow-lg p-2 min-w-[220px]">
                    {resourcesDropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="flex flex-col gap-0.5 px-4 py-2.5 rounded-md hover:bg-[#1C2838] transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language selector */}
          <div
            className="relative"
            onMouseEnter={() => setLangDropdownOpen(true)}
            onMouseLeave={() => setLangDropdownOpen(false)}
          >
            <button
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
              aria-label="Select language"
            >
              <img src={languages.find(l => l.code === currentLang)?.flag} alt="" className="w-5 h-auto rounded-sm opacity-70 saturate-[0.6]" />
              <span>{languages.find(l => l.code === currentLang)?.label}</span>
            </button>
            <AnimatePresence>
              {langDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="bg-[#162231] border border-border rounded-lg shadow-lg p-2 min-w-[140px]">
                    {languages.filter(l => l.code !== currentLang).map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => { i18n.changeLanguage(lang.code); setLangDropdownOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 rounded-md hover:bg-[#1C2838] transition-colors text-sm text-foreground"
                      >
                        <img src={lang.flag} alt="" className="w-5 h-auto rounded-sm opacity-70 saturate-[0.6]" />
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href="https://app.cetasol.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
          >
            {t('nav.logIn')}
          </a>
          <Link
            to="/book-a-demo"
            className="text-sm font-medium bg-white text-background px-5 py-2 rounded-md hover:bg-white/90 transition-colors"
          >
            {t('nav.bookDemo')}
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <nav className="flex flex-col gap-4 p-6">
              {/* Language selector mobile */}
              <div className="flex gap-3 mb-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={`text-sm font-medium transition-colors ${currentLang === lang.code ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <img src={lang.flag} alt="" className="w-5 h-auto rounded-sm opacity-70 saturate-[0.6] inline mr-1" /> {lang.label}
                  </button>
                ))}
              </div>

              <div>
                <button
                  onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                  className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>{t('nav.solutions')}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${mobileSolutionsOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileSolutionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 flex flex-col gap-2"
                    >
                      <Link
                        to="/solutions"
                        onClick={() => setMenuOpen(false)}
                        className="block py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t('mobileNav.overview')}
                      </Link>
                      {solutionDropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMenuOpen(false)}
                          className="block py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <button
                  onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
                  className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>{t('nav.company')}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${mobileCompanyOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileCompanyOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 flex flex-col gap-2"
                    >
                      {companyDropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMenuOpen(false)}
                          className="block py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <button
                  onClick={() => setMobileInsightOpen(!mobileInsightOpen)}
                  className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>{t('nav.resources')}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${mobileInsightOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileInsightOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 flex flex-col gap-2"
                    >
                      {resourcesDropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMenuOpen(false)}
                          className="block py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <a
                href="https://app.cetasol.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('nav.logIn')}
              </a>
              <Link
                to="/book-a-demo"
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium bg-primary text-primary-foreground px-5 py-2.5 rounded-md text-center hover:bg-primary-hover transition-colors"
              >
                {t('nav.bookDemo')}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
